// SosToolkitPlayer.jsx - FreiseinApp - v1
import React, { useState, useEffect, useRef } from "react";
import BackButton from "./BackButton";
import "./SosToolkitPlayer.css";

// ─── Tracks & Dateien ────────────────────────────────────────────────────────
// Passe die src-Pfade an nachdem du die MP3s auf deinen Server hochgeladen hast
const TRACKS = [
  {
    id: 1,
    title: "Meditation 1 – Ankommen & Erden",
    description: "Für akute Angst-Momente – bringt dich sofort in den Körper zurück.",
    src: "/media/sos-toolkit/meditation-01.mp3",
    duration: "12 min",
    cover: "/media/sos-toolkit/cover.jpg",
  },
  {
    id: 2,
    title: "Meditation 2 – Den Moment lösen",
    description: "Wenn Gedanken sich im Kreis drehen – diese Übung unterbricht den Kreislauf.",
    src: "/media/sos-toolkit/meditation-02.mp3",
    duration: "15 min",
    cover: "/media/sos-toolkit/cover.jpg",
  },
  {
    id: 3,
    title: "Meditation 3 – Sicherheit aufbauen",
    description: "Für die ruhigeren Momente – langfristige innere Stabilität aufbauen.",
    src: "/media/sos-toolkit/meditation-03.mp3",
    duration: "18 min",
    cover: "/media/sos-toolkit/cover.jpg",
  },
];

const PDF_URL = "/media/sos-toolkit/notfall-aktionsplan.pdf";
const BONUS_URL = "/media/sos-toolkit/bonus-leitfaden.pdf";

// ─── Hilfsfunktion: Zeit formatieren ─────────────────────────────────────────
function formatTime(seconds) {
  if (isNaN(seconds)) return "0:00";
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

// ─── Komponente ──────────────────────────────────────────────────────────────
const SosToolkitPlayer = () => {
  const audioRef = useRef(null);
  const [currentTrack, setCurrentTrack] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  // ── Audio laden wenn Track wechselt ────────────────────────────────────────
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const wasPlaying = isPlaying;
    audio.src = TRACKS[currentTrack].src;
    audio.load();
    setCurrentTime(0);
    setDuration(0);
    if (wasPlaying) {
      audio.play().catch(() => {});
    }
    updateMediaSession(currentTrack);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTrack]);

  // ── Audio Event Listener ───────────────────────────────────────────────────
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const onTimeUpdate = () => setCurrentTime(audio.currentTime);
    const onDurationChange = () => setDuration(audio.duration);
    const onPlay = () => { setIsPlaying(true); setIsLoading(false); }
    const onPause = () => setIsPlaying(false);
    const onWaiting = () => setIsLoading(true);
    const onCanPlay = () => setIsLoading(false);
    const onEnded = () => {
      setIsPlaying(false);
      if (currentTrack < TRACKS.length - 1) {
        setCurrentTrack((t) => t + 1);
      }
    };

    audio.addEventListener("timeupdate", onTimeUpdate);
    audio.addEventListener("durationchange", onDurationChange);
    audio.addEventListener("play", onPlay);
    audio.addEventListener("pause", onPause);
    audio.addEventListener("waiting", onWaiting);
    audio.addEventListener("canplay", onCanPlay);
    audio.addEventListener("ended", onEnded);

    return () => {
      audio.removeEventListener("timeupdate", onTimeUpdate);
      audio.removeEventListener("durationchange", onDurationChange);
      audio.removeEventListener("play", onPlay);
      audio.removeEventListener("pause", onPause);
      audio.removeEventListener("waiting", onWaiting);
      audio.removeEventListener("canplay", onCanPlay);
      audio.removeEventListener("ended", onEnded);
    };
  }, [currentTrack]);

  // ── Media Session API (Lock Screen Controls) ───────────────────────────────
  const updateMediaSession = (trackIndex) => {
    if (!("mediaSession" in navigator)) return;
    const track = TRACKS[trackIndex];
    navigator.mediaSession.metadata = new window.MediaMetadata({
      title: track.title,
      artist: "Georg Wendt – verlustangst24.de",
      album: "SOS Toolkit",
      artwork: [
        { src: track.cover, sizes: "512x512", type: "image/jpeg" },
      ],
    });
    navigator.mediaSession.setActionHandler("play", () => audioRef.current?.play());
    navigator.mediaSession.setActionHandler("pause", () => audioRef.current?.pause());
    navigator.mediaSession.setActionHandler("previoustrack", () => {
      if (trackIndex > 0) setCurrentTrack((t) => t - 1);
    });
    navigator.mediaSession.setActionHandler("nexttrack", () => {
      if (trackIndex < TRACKS.length - 1) setCurrentTrack((t) => t + 1);
    });
    navigator.mediaSession.setActionHandler("seekto", (details) => {
      if (audioRef.current && details.seekTime !== undefined) {
        audioRef.current.currentTime = details.seekTime;
      }
    });
  };

  // ── Playback Controls ──────────────────────────────────────────────────────
  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) {
      audio.pause();
    } else {
      updateMediaSession(currentTrack);
      audio.play().catch((e) => console.error("Play error:", e));
    }
  };

  const handleSeek = (e) => {
    const audio = audioRef.current;
    if (!audio || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const ratio = x / rect.width;
    audio.currentTime = ratio * duration;
  };

  const handleTrackSelect = (index) => {
    if (index === currentTrack) {
      togglePlay();
    } else {
      setCurrentTrack(index);
      setIsPlaying(true);
      setTimeout(() => {
        audioRef.current?.play().catch(() => {});
      }, 100);
    }
  };

  const progress = duration ? (currentTime / duration) * 100 : 0;

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="sos-wrapper">
      <audio ref={audioRef} preload="metadata" />
      <BackButton to="/client-dashboard" />

      {/* Header */}
      <div className="sos-header">
        <div className="sos-badge">SOS Toolkit</div>
        <h1 className="sos-title">Sofort-Hilfe bei Verlustangst</h1>
        <p className="sos-subtitle">Georg Wendt · verlustangst24.de</p>
      </div>

      {/* Player */}
      <div className="sos-player">
        <div className="sos-track-info">
          <span className="sos-track-num">Track {currentTrack + 1} / {TRACKS.length}</span>
          <h2 className="sos-track-title">{TRACKS[currentTrack].title}</h2>
          <p className="sos-track-desc">{TRACKS[currentTrack].description}</p>
        </div>

        {/* Progress Bar */}
        <div className="sos-progress-bar" onClick={handleSeek}>
          <div className="sos-progress-fill" style={{ width: `${progress}%` }} />
          <div className="sos-progress-thumb" style={{ left: `${progress}%` }} />
        </div>
        <div className="sos-time-row">
          <span>{formatTime(currentTime)}</span>
          <span>{formatTime(duration)}</span>
        </div>

        {/* Controls */}
        <div className="sos-controls">
          <button
            className="sos-btn-secondary"
            onClick={() => currentTrack > 0 && setCurrentTrack((t) => t - 1)}
            disabled={currentTrack === 0}
            aria-label="Vorheriger Track"
          >⏮</button>

          <button
            className="sos-btn-play"
            onClick={togglePlay}
            aria-label={isPlaying ? "Pause" : "Play"}
          >
            {isLoading ? (
              <span className="sos-spinner" />
            ) : isPlaying ? "⏸" : "▶"}
          </button>

          <button
            className="sos-btn-secondary"
            onClick={() => currentTrack < TRACKS.length - 1 && setCurrentTrack((t) => t + 1)}
            disabled={currentTrack === TRACKS.length - 1}
            aria-label="Nächster Track"
          >⏭</button>
        </div>
      </div>

      {/* Track Liste */}
      <div className="sos-tracklist">
        <h3 className="sos-section-title">Alle Meditationen</h3>
        {TRACKS.map((track, index) => (
          <div
            key={track.id}
            className={`sos-track-item ${index === currentTrack ? "active" : ""}`}
            onClick={() => handleTrackSelect(index)}
          >
            <div className="sos-track-icon">
              {index === currentTrack && isPlaying ? "⏸" : "▶"}
            </div>
            <div className="sos-track-meta">
              <span className="sos-track-name">{track.title}</span>
              <span className="sos-track-dur">{track.duration}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Downloads */}
      <div className="sos-downloads">
        <h3 className="sos-section-title">Downloads</h3>
        <a href={PDF_URL} download className="sos-download-btn">
          📋 Notfall-Aktionsplan (PDF)
        </a>
        <a href={BONUS_URL} download className="sos-download-btn">
          🎁 Bonus-Leitfaden: 5 Denkmuster (PDF)
        </a>
      </div>
    </div>
  );
};

export default SosToolkitPlayer;
